import { getVariant } from "../../lib/variant.ts";

type Props = {
  className?: string;
  style?: React.CSSProperties;
};

const BARB_COUNT = 30;

function buildBarbs(count: number) {
  const lines: { x1: number; y1: number; x2: number; y2: number }[] = [];
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1);
    const y = 22 + t * 232;
    const lenFactor = Math.sin(t * Math.PI);
    const len = 4 + lenFactor * 24;
    const angleDeg = -24 - t * 6;
    const rad = (angleDeg * Math.PI) / 180;
    const dx = len * Math.cos(rad);
    const dy = len * Math.sin(rad);
    lines.push({ x1: 50, y1: y, x2: 50 - dx, y2: y - dy });
    lines.push({ x1: 50, y1: y, x2: 50 + dx, y2: y - dy });
  }
  return lines;
}

const BARBS = buildBarbs(BARB_COUNT);
const BARBS_DENSE = buildBarbs(54);

function FeatherBase({ className, style }: Props) {
  return (
    <svg
      viewBox="0 0 100 280"
      className={className}
      style={style}
      aria-hidden
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="featherGold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E6C971" />
          <stop offset="50%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#A8861F" />
        </linearGradient>
      </defs>

      <path
        d="M50 12 C 24 70, 20 150, 32 210 C 36 232, 44 252, 50 270 C 56 252, 64 232, 68 210 C 80 150, 76 70, 50 12 Z"
        fill="url(#featherGold)"
        opacity="0.12"
      />

      <path
        d="M50 14 Q 49 140 50 268"
        stroke="#D4AF37"
        strokeWidth="0.9"
        fill="none"
        opacity="0.65"
      />

      <g
        stroke="#D4AF37"
        strokeWidth="0.55"
        strokeLinecap="round"
        opacity="0.5"
      >
        {BARBS.map((b, i) => (
          <line key={i} x1={b.x1} y1={b.y1} x2={b.x2} y2={b.y2} />
        ))}
      </g>

      <circle cx="50" cy="270" r="1.4" fill="#A8861F" opacity="0.7" />
    </svg>
  );
}

function FeatherV1({ className, style }: Props) {
  return (
    <svg
      viewBox="0 0 100 280"
      className={className}
      style={style}
      aria-hidden
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="featherV1Body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F2D98A" />
          <stop offset="45%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#6E5412" />
        </linearGradient>
        <linearGradient id="featherV1Highlight" x1="0.3" y1="0" x2="0.7" y2="1">
          <stop offset="0%" stopColor="#FFF6D8" stopOpacity="0.85" />
          <stop offset="55%" stopColor="#FFF6D8" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="featherV1Shadow" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#2E220A" stopOpacity="0.35" />
          <stop offset="50%" stopColor="#2E220A" stopOpacity="0" />
          <stop offset="100%" stopColor="#2E220A" stopOpacity="0.35" />
        </linearGradient>
        <radialGradient id="featherV1Glow" cx="0.5" cy="0.4" r="0.8">
          <stop offset="0%" stopColor="#F5D88C" stopOpacity="0.45" />
          <stop offset="60%" stopColor="#F5D88C" stopOpacity="0" />
        </radialGradient>
        <filter id="featherV1Blur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3.5" />
        </filter>
        <filter
          id="featherV1DropShadow"
          x="-40%"
          y="-20%"
          width="180%"
          height="140%"
        >
          <feDropShadow
            dx="2.2"
            dy="3.4"
            stdDeviation="2.4"
            floodColor="#1a140a"
            floodOpacity="0.38"
          />
        </filter>
      </defs>

      <ellipse
        cx="52"
        cy="150"
        rx="28"
        ry="120"
        fill="url(#featherV1Glow)"
        filter="url(#featherV1Blur)"
      />

      <g filter="url(#featherV1DropShadow)">
        <path
          d="M52 14 C 26 72, 22 152, 34 212 C 38 234, 46 254, 52 272 C 58 254, 66 234, 70 212 C 82 152, 78 72, 52 14 Z"
          fill="url(#featherV1Body)"
          opacity="0.28"
        />
        <path
          d="M50 12 C 24 70, 20 150, 32 210 C 36 232, 44 252, 50 270 C 56 252, 64 232, 68 210 C 80 150, 76 70, 50 12 Z"
          fill="url(#featherV1Highlight)"
          opacity="0.9"
        />
        <path
          d="M50 12 C 24 70, 20 150, 32 210 C 36 232, 44 252, 50 270 C 56 252, 64 232, 68 210 C 80 150, 76 70, 50 12 Z"
          fill="url(#featherV1Shadow)"
          opacity="0.7"
        />

        <path
          d="M50 14 Q 48 140 50 268"
          stroke="#6E5412"
          strokeWidth="1.6"
          fill="none"
          opacity="0.55"
        />
        <path
          d="M50 14 Q 49 140 50 268"
          stroke="#F5D88C"
          strokeWidth="0.55"
          fill="none"
          opacity="0.95"
        />

        <g strokeLinecap="round">
          {BARBS_DENSE.map((b, i) => {
            const t = (b.y1 - 22) / 232;
            const width = 0.35 + Math.sin(t * Math.PI) * 0.55;
            return (
              <line
                key={`shadow-${i}`}
                x1={b.x1 + 0.6}
                y1={b.y1 + 0.8}
                x2={b.x2 + 0.6}
                y2={b.y2 + 0.8}
                stroke="#2E220A"
                strokeWidth={width}
                opacity="0.42"
              />
            );
          })}
          {BARBS_DENSE.map((b, i) => {
            const t = (b.y1 - 22) / 232;
            const width = 0.32 + Math.sin(t * Math.PI) * 0.52;
            return (
              <line
                key={`gold-${i}`}
                x1={b.x1}
                y1={b.y1}
                x2={b.x2}
                y2={b.y2}
                stroke="url(#featherV1Body)"
                strokeWidth={width}
                opacity="0.82"
              />
            );
          })}
        </g>

        <circle cx="50" cy="270" r="2.2" fill="#2E220A" opacity="0.75" />
        <circle cx="49.6" cy="269.4" r="1.1" fill="#F5D88C" opacity="0.9" />
      </g>
    </svg>
  );
}

function FeatherV2({ className, style }: Props) {
  return (
    <div
      className={`feather-v2-stage ${className ?? ""}`}
      style={style}
      aria-hidden
    >
      <svg
        viewBox="0 0 100 280"
        className="feather-v2-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="featherV2Body" x1="0.2" y1="0" x2="0.8" y2="1">
            <stop offset="0%" stopColor="#FFF0B8" />
            <stop offset="40%" stopColor="#E6C971" />
            <stop offset="70%" stopColor="#B08A22" />
            <stop offset="100%" stopColor="#4A3608" />
          </linearGradient>
          <linearGradient
            id="featherV2Specular"
            x1="0.2"
            y1="0.1"
            x2="0.8"
            y2="0.9"
          >
            <stop offset="0%" stopColor="#FFFBE0" stopOpacity="0" />
            <stop offset="40%" stopColor="#FFFBE0" stopOpacity="0.95" />
            <stop offset="60%" stopColor="#FFFBE0" stopOpacity="0" />
          </linearGradient>
          <filter id="featherV2DropShadow">
            <feDropShadow
              dx="3"
              dy="5"
              stdDeviation="3"
              floodColor="#0e0a02"
              floodOpacity="0.45"
            />
          </filter>
        </defs>

        <g filter="url(#featherV2DropShadow)">
          <path
            d="M50 12 C 24 70, 20 150, 32 210 C 36 232, 44 252, 50 270 C 56 252, 64 232, 68 210 C 80 150, 76 70, 50 12 Z"
            fill="url(#featherV2Body)"
            opacity="0.32"
          />
          <path
            d="M50 12 C 24 70, 20 150, 32 210 C 36 232, 44 252, 50 270 C 56 252, 64 232, 68 210 C 80 150, 76 70, 50 12 Z"
            fill="url(#featherV2Specular)"
            opacity="0.85"
          />

          <path
            d="M50 14 Q 49 140 50 268"
            stroke="#F5D88C"
            strokeWidth="0.9"
            fill="none"
            opacity="0.9"
          />
          <path
            d="M50 14 Q 49 140 50 268"
            stroke="#3a2a08"
            strokeWidth="0.35"
            fill="none"
            opacity="0.7"
          />

          <g stroke="url(#featherV2Body)" strokeLinecap="round">
            {BARBS_DENSE.map((b, i) => {
              const t = (b.y1 - 22) / 232;
              const width = 0.3 + Math.sin(t * Math.PI) * 0.5;
              return (
                <line
                  key={i}
                  x1={b.x1}
                  y1={b.y1}
                  x2={b.x2}
                  y2={b.y2}
                  strokeWidth={width}
                  opacity="0.78"
                />
              );
            })}
          </g>
        </g>

        <circle cx="50" cy="270" r="1.8" fill="#4A3608" opacity="0.85" />
      </svg>
    </div>
  );
}

export default function Feather(props: Props) {
  const v = getVariant();
  if (v === "original") return <FeatherBase {...props} />;
  if (v === "2") return <FeatherV2 {...props} />;
  return <FeatherV1 {...props} />;
}
